import { LoadingButton, Stack } from "@altimateai/lego";
import { Button, Textarea } from "@altimateai/lego";
import { FC, ReactNode, useState } from "react";
import { useChatContext } from "../../ChatProvider";
import { useFeedback } from "../../hooks/useFeedback";
import { IconThumbDown, IconThumbUp } from "@tabler/icons-react";

interface ChatFeedbackActionsProps {
  messageId: string;
  children?: ReactNode;
}

export const ChatFeedbackActions: FC<ChatFeedbackActionsProps> = ({
  messageId,
  children,
}) => {
  const { components } = useChatContext();
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [selectedRating, setSelectedRating] = useState<
    "up" | "down" | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { submitFeedback } = useFeedback();

  const handleRatingClick = async (rating: "up" | "down") => {
    if (rating === selectedRating) {
      setSelectedRating(undefined);
      setShowComment(false);
      await submitFeedback(messageId, { rating: undefined });
    } else {
      setSelectedRating(rating);
      setShowComment(true);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!selectedRating) return;

    setIsSubmitting(true);
    try {
      await submitFeedback(messageId, {
        rating: selectedRating,
        comment: comment.trim() || undefined,
      });
      setShowComment(false);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (components?.feedback) {
    return components.feedback;
  }

  return (
    <>
      <Stack
        direction="row"
        justify={"end"}
        role="group"
        aria-label="Message feedback"
      >
        {children}
        <Button
          variant="ghost"
          size="sm"
          className={
            selectedRating === "up"
              ? "al-bg-green-100 al-text-green-600"
              : "al-text-gray-500"
          }
          onClick={() => handleRatingClick("up")}
          disabled={isSubmitting}
          title="Like response"
          aria-pressed={selectedRating === "up"}
        >
          <IconThumbUp aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={
            selectedRating === "down"
              ? "al-bg-red-100 al-text-red-600"
              : "al-text-gray-500"
          }
          onClick={() => handleRatingClick("down")}
          disabled={isSubmitting}
          title="Dislike response"
          aria-pressed={selectedRating === "down"}
        >
          <IconThumbDown aria-hidden="true" />
        </Button>
      </Stack>
      {showComment && (
        <div className="al-mt-2 al-space-y-2 al-w-full al-basis-full al-px-2">
          <Textarea
            placeholder="Tell us more about your feedback..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="al-min-h-20"
            disabled={isSubmitting}
            aria-label="Feedback comment"
            autoFocus
          />
          <Stack direction="row">
            <LoadingButton
              size="sm"
              onClick={handleSubmitFeedback}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              loading={isSubmitting}
              loadingText={"Submitting..."}
            >
              Submit Feedback
            </LoadingButton>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComment(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Stack>
        </div>
      )}
    </>
  );
};
